// betonijerry/src/pages/TarjouspyyntoEsikatselu.jsx
// PUBLIC tokenized preview of one tarjouspyyntö — the provider
// email CTA lands here (spec: workspace docs/superpowers/specs/
// 2026-07-14-jerry-tarjouspyynto-preview-link-design.md §3–§5).
// Viewing needs no login, and since fb#1268 NEITHER DOES REPLYING: the offer and
// decline below post with the capability token. The customer's contact details
// are released by SUBMITTING A BID, not by clicking "reveal" — handing the lead
// out for free is why 12 deliberate reveal clicks produced 0 offers and 0 declines.
// After login: server-side access check → auto company switch → authenticated
// /tarjouspyynnot/:id with the local draft prefilled.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
    Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import SendIcon from "@mui/icons-material/Send";
import BlockIcon from "@mui/icons-material/Block";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { eurosToCents, isSubmittablePrice } from "@ibetoni/jerry-client";
import { useAuth } from "src/context/AuthContext";
import { useAuthModal } from "src/context/AuthModalContext";
import { useLang, useT } from "src/i18n";
import { providerApi } from "src/utils/tarjouspyynnot/providerApi";
import { decodeJwtPayload } from "src/utils/auth/jwtClaims";
import { readOfferDraft, writeOfferDraft } from "src/utils/tarjouspyynnot/offerDraft";
import { localizeDecimal } from "src/utils/valut/valutFormatters";
import { captureError } from "src/utils/errorReporting";
import FrostedCard from "src/components/quote/FrostedCard";
import RequestRecap from "src/components/tarjouspyynnot/RequestRecap";
import { amberCtaSx } from "src/components/quote/amberCtaSx";

// Phone-worksite tap target (DESIGN.md "mobile-worksite first"): MUI's medium
// outlined/text buttons are 36px / 31px tall, which is what the critique measured.
const TAP_TARGET_SX = { minHeight: 44 };

// The one-line job summary under the heading: what a pumping company prices by.
// On a phone it is the working-memory bridge — the action card renders FIRST
// there (Grid `order` below), so the specs would otherwise be a screen away.
function summaryLine(r, lang, t) {
    return [
        r.formattedAddress || r.osoite,
        r.totalM3 ? t("provider.preview.summaryAmount", { m3: localizeDecimal(r.totalM3, lang) }) : null,
        r.requiredPuomi ? t("provider.listCard.pumpBoomSuffix", { m: localizeDecimal(r.requiredPuomi, lang) }) : null,
        r.requiredLinja ? t("provider.listCard.pumpLineSuffix", { m: localizeDecimal(r.requiredLinja, lang) }) : null,
        r.pumppuKesto ? t("provider.preview.summaryDuration", { h: localizeDecimal(r.pumppuKesto, lang) }) : null,
    ].filter(Boolean).join(" · ");
}

export default function TarjouspyyntoEsikatselu() {
    const [searchParams] = useSearchParams();
    const previewToken = searchParams.get("token") || "";
    // The provider email carries a second link, "En tarjoa tähän", that lands
    // here with action=decline so the decline is reachable from the inbox rather
    // than only after reading the whole page. Any other value is ignored — an
    // unrecognised param has always been inert here.
    const declineIntent = searchParams.get("action") === "decline";
    const navigate = useNavigate();
    const { token, isAuthenticated, ownerAsiakasId, user, switchCompany, logout } = useAuth();
    const { openAuthModal } = useAuthModal();
    const t = useT();
    const { lang } = useLang();

    // Unverified decode is safe here: it only picks WHICH id to navigate to
    // post-login — the server re-authorizes everything (and it is the only way
    // to know the target for an EXPIRED token).
    const claims = useMemo(() => decodeJwtPayload(previewToken), [previewToken]);
    const requestId = Number.isInteger(claims?.pumppuRequestId) ? claims.pumppuRequestId : null;

    const [view, setView] = useState("loading"); // loading | ready | expired | invalid | denied | unavailable
    const [preview, setPreview] = useState(null); // { request, ownStatus, asiakasId }
    const [price, setPrice] = useState("");
    const [extraNotes, setExtraNotes] = useState("");
    // Post-login continuation flag. Carried an "offer" | "decline" intent until
    // fb#1455; declining is inline over the token now, so the only continuation
    // left is "open the request", and a boolean says that without pretending.
    const [pendingLogin, setPendingLogin] = useState(false);
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState(null);
    // Field-level, so the price error renders AT the price field (TextField
    // error + helperText) instead of below the third button; cleared on edit.
    const [priceError, setPriceError] = useState(null);
    // The provider's price is a BINDING offer (CLAUDE.md legal model), so the
    // send is two steps: "Lähetä tarjous" validates and shows an inline confirm
    // that names the amount; "Kyllä, lähetä" posts. Inline, not a modal — the
    // provider keeps the specs and the draft in view while deciding.
    const [confirming, setConfirming] = useState(false);
    // Set by a 409 on the offer route: the token has no person behind it, so
    // the form is swapped for the cannot-bid block (same block the GET flag
    // selects up front) rather than stacked under a red alert that contradicts
    // the form's own copy — the Betomik #74 screen.
    const [bidRefused, setBidRefused] = useState(false);
    const [revealBusy, setRevealBusy] = useState(false);
    const [revealError, setRevealError] = useState(null);
    const [interestSent, setInterestSent] = useState(false);
    const [declineOpen, setDeclineOpen] = useState(false);
    // Set once the visitor closes the auto-opened dialog, so an action=decline
    // link that they thought better of does not spring back open. Derived
    // opening rather than an effect: this repo's lint forbids a synchronous
    // setState inside useEffect (see the async wrapper on the loader below).
    const [declineIntentDismissed, setDeclineIntentDismissed] = useState(false);
    const [declineReason, setDeclineReason] = useState("");
    const [outcome, setOutcome] = useState(null); // null | "offered" | "declined"

    // Load the preview DTO (anonymous).
    useEffect(() => {
        let cancelled = false;
        // Wrapped in inner async fn so react-hooks/set-state-in-effect does not
        // flag the synchronous setView calls (repo convention — see MyValut.jsx).
        async function run() {
            if (!previewToken || !requestId) { setView("invalid"); return; }
            try {
                const data = await providerApi(null).getPreview(previewToken);
                if (cancelled) return;
                setPreview(data);
                setView("ready");
                const draft = readOfferDraft(requestId);
                if (draft) { setPrice(draft.price); setExtraNotes(draft.extraNotes); }
            } catch (err) {
                if (cancelled) return;
                if (err.status === 401 && err.message === "PREVIEW_TOKEN_EXPIRED") { setView("expired"); return; }
                if (err.status === 401 || err.status === 400 || err.status === 404) { setView("invalid"); return; }
                captureError(err, { tags: { entity: "pumppuRequest", operation: "preview_get" } });
                setView("invalid");
            }
        }
        run();
        return () => { cancelled = true; };
    }, [previewToken, requestId]);

    useEffect(() => {
        document.title = t("provider.preview.pageTitle");
        return () => { document.title = t("provider.preview.pageTitleFallback"); };
    }, [t]);

    // Persist the draft on every edit — it must survive the login round-trip.
    const updatePrice = (v) => { setPrice(v); setPriceError(null); writeOfferDraft(requestId, { price: v, extraNotes }); };
    const updateExtraNotes = (v) => { setExtraNotes(v); writeOfferDraft(requestId, { price, extraNotes: v }); };

    // Post-login continuation: server access check → company switch → detail.
    const runAccess = useCallback(async () => {
        const targetAsiakasId = Number(claims?.asiakasId);
        if (!requestId || !Number.isInteger(targetAsiakasId)) { setView("invalid"); return; }
        setBusy(true);
        setActionError(null);
        try {
            const result = await providerApi(token).checkPreviewAccess(requestId, previewToken);
            // Only a 400 (malformed/mismatched token) means the LINK is bad —
            // distinct from a real "no access" answer (success:true,
            // data.ok:false). A 5xx (or Cloudflare's 502 mask, or a network
            // failure with no status) is transient: keep the page and show a
            // retry error instead of the "linkki ei kelpaa" dead end.
            if (!result.success) {
                if (result.status === 400) { setView("invalid"); return; }
                setActionError(t("provider.preview.genericRetryError"));
                return;
            }
            // A de-listed (no-longer-recipient) company is a benign, non-security
            // outcome — its own neutral view, distinct from the "denied" (missing
            // permission) copy. Server flags it with reason: "not_recipient".
            if (result.data?.reason === "not_recipient") { setView("unavailable"); return; }
            if (!result.data?.ok) { setView("denied"); return; }
            if (Number(ownerAsiakasId) !== targetAsiakasId) {
                await switchCompany(targetAsiakasId);
            }
            // Navigate AFTER the switch resolves so /tarjouspyynnot/:id mounts
            // with the new company identity (stale-asiakasId trap, spec §5.2).
            navigate(`/tarjouspyynnot/${requestId}`, {
                // No intent is carried any more (fb#1455): declining happens inline
                // on this page over the token, so every handleCta call site passes
                // "offer" and the decline branch here was unreachable.
                state: undefined,
            });
        } catch (err) {
            // Expected during the login round-trip if the session went stale
            // mid-flow — logout() sends the user back to the auth gate rather
            // than surfacing it as an unexpected error.
            if (err.status === 401) { logout(); return; }
            captureError(err, {
                tags: { entity: "pumppuRequest", operation: "preview_access" },
                extra: { pumppuRequestId: requestId },
            });
            setActionError(t("provider.preview.genericRetryError"));
        } finally {
            setBusy(false);
        }
    }, [token, ownerAsiakasId, claims, requestId, previewToken, switchCompany, navigate, logout, t]);

    // AuthModal's onSuccess fires before this component sees the fresh token,
    // so it only records that a login happened; this effect continues once useAuth()
    // delivers the authenticated context.
    useEffect(() => {
        if (!pendingLogin || !token) return;
        // Wrapped in inner async fn so react-hooks/set-state-in-effect does not
        // flag the synchronous setPendingLogin(false) call.
        async function run() {
            setPendingLogin(false);
            await runAccess();
        }
        run();
    }, [pendingLogin, token, runAccess]);

    const handleCta = () => {
        if (isAuthenticated) { runAccess(); return; }
        openAuthModal({ onSuccess: () => setPendingLogin(true) });
    };

    // Records INTEREST: stamps this company's view-claim server-side (the first
    // one emails the customer "kiinnostui tarjouspyynnöstänne"). It no longer
    // returns the customer's contact details — a bid does that (fb#1268).
    const handleReveal = async () => {
        setRevealBusy(true);
        setRevealError(null);
        try {
            const result = await providerApi(null).revealPreview(previewToken);
            // MERGE, never replace (same as the offer-success path): the reveal
            // envelope does not have to carry every GET /preview field, and the
            // bid gate below is default-allow — replacing the state dropped
            // canBidWithLink and re-enabled the price form for a shared-inbox
            // token, whose submit then 409'd (Betomik, request #74).
            if (result.success) { setPreview((p) => ({ ...p, ...result.data })); setInterestSent(true); return; }
            // De-listed mid-session (no longer a recipient) — same benign copy
            // as the post-login access check; 400 means the LINK itself is bad.
            if (result.status === 404) { setView("unavailable"); return; }
            if (result.status === 400) { setView("invalid"); return; }
            setRevealError(t("provider.preview.genericRetryError"));
        } catch (err) {
            // mutate() rethrows only 401s — expired vs invalid token.
            if (err.status === 401) {
                setView(err.message === "PREVIEW_TOKEN_EXPIRED" ? "expired" : "invalid");
                return;
            }
            captureError(err, {
                tags: { entity: "pumppuRequest", operation: "preview_reveal" },
                extra: { pumppuRequestId: requestId },
            });
            setRevealError(t("provider.preview.genericRetryError"));
        } finally {
            setRevealBusy(false);
        }
    };

    // Shared failure mapping for the two tokenized write paths. A 404 means this
    // company was de-listed from the request; 400 means the link itself is bad.
    const handleActionFailure = (result, operation) => {
        if (result.status === 404) { setView("unavailable"); return; }
        if (result.status === 400) { setView("invalid"); return; }
        setActionError(result.error || t("provider.preview.genericRetryError"));
        captureError(new Error(`preview ${operation} failed: ${result.error || result.status}`), {
            tags: { entity: "pumppuRequest", operation: `preview_${operation}` },
            extra: { pumppuRequestId: requestId, status: result.status },
        });
    };

    // Shared with OfferForm rather than re-derived (fb#1462): isSubmittablePrice
    // also applies MAX_PRICE_CENTS, so an over-max bid is refused here instead
    // of making a round trip to be refused by the route.
    const priceCents = eurosToCents(price);

    // Step 1 of the send: validate at the field, then ask for the binding confirm.
    const startSubmitOffer = (e) => {
        e?.preventDefault();
        if (!isSubmittablePrice(priceCents)) {
            setPriceError(t("provider.preview.priceRequiredError"));
            return;
        }
        setActionError(null);
        setConfirming(true);
    };

    // Step 2: submit the bid straight from the email — no login (fb#1268).
    // Success is the ONLY path that returns the customer's contact details.
    const handleSubmitOffer = async () => {
        setBusy(true);
        setActionError(null);
        try {
            const result = await providerApi(null).offerViaPreview(previewToken, {
                priceCents,
                extraNotes: extraNotes || undefined,
            });
            if (result.success) {
                // Only the request payload changes locally (fb#1452). Setting
                // ownStatus: "offered" here ALSO fired the pre-existing "you already
                // have an offer, log in to view it" block, so a provider who had just
                // bid with no login was told to log in, above the success message.
                // outcome is the single "I just acted" signal — which is exactly how
                // the decline path already worked. A reload still shows the correct
                // already-offered block, because the SERVER returns ownStatus.
                setPreview((p) => ({ ...p, request: result.data.request }));
                setOutcome("offered");
                return;
            }
            // The company's notification address has no person behind it (a shared
            // inbox or the billing email), so there is nobody to attribute the bid
            // to. `ib jerry admin detail` flags exactly these companies (fb#1265).
            if (result.status === 409) {
                setBidRefused(true);
                return;
            }
            handleActionFailure(result, "offer");
        } catch (err) {
            if (err.status === 401) {
                setView(err.message === "PREVIEW_TOKEN_EXPIRED" ? "expired" : "invalid");
                return;
            }
            captureError(err, {
                tags: { entity: "pumppuRequest", operation: "preview_offer" },
                extra: { pumppuRequestId: requestId },
            });
            setActionError(t("provider.preview.genericRetryError"));
        } finally {
            setBusy(false);
            setConfirming(false);
        }
    };

    // Declining needs no login either. Until this existed the decline path was
    // login-gated and had never once been used — 0 declines across 58 fan-out rows.
    const handleSubmitDecline = async () => {
        if (declineReason.trim() === "") {
            setActionError(t("provider.preview.declineReasonRequiredError"));
            return;
        }
        setBusy(true);
        setActionError(null);
        try {
            const result = await providerApi(null).declineViaPreview(previewToken, declineReason);
            if (result.success) {
                setDeclineOpen(false);
                setOutcome("declined");
                return;
            }
            handleActionFailure(result, "decline");
        } catch (err) {
            if (err.status === 401) {
                setView(err.message === "PREVIEW_TOKEN_EXPIRED" ? "expired" : "invalid");
                return;
            }
            captureError(err, {
                tags: { entity: "pumppuRequest", operation: "preview_decline" },
                extra: { pumppuRequestId: requestId },
            });
            setActionError(t("provider.preview.genericRetryError"));
        } finally {
            setBusy(false);
        }
    };

    // A worksite connection can take seconds here; a blank page between header
    // and footer read as "the link is broken" (critique H1). role=status so a
    // screen reader hears the wait as well.
    if (view === "loading") {
        return (
            <FrostedCard sx={{ p: 3, maxWidth: 560, mx: "auto" }} role="status" aria-busy="true">
                <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
                    <CircularProgress size={22} color="warning" />
                    <Typography>{t("provider.preview.loadingText")}</Typography>
                </Stack>
            </FrostedCard>
        );
    }

    if (view === "invalid") {
        return (
            <FrostedCard sx={{ p: 3, maxWidth: 560, mx: "auto" }}>
                <Alert severity="error">
                    {isAuthenticated
                        ? t("provider.preview.invalidLinkAuthenticatedNotice")
                        : t("provider.preview.invalidLinkNotice")}
                </Alert>
                {/* No usable token here, so a session goes to the list, not the request.
                    Amber: the one action on the screen is the lamp in every terminal
                    state (invalid, expired, offered), never teal in one and amber in
                    the next. */}
                <Button
                    sx={[amberCtaSx, { mt: 2 }]}
                    variant="contained"
                    startIcon={<LoginIcon />}
                    onClick={() => (isAuthenticated ? navigate("/tarjouspyynnot") : openAuthModal())}
                >
                    {isAuthenticated ? t("provider.preview.openRequestsCta") : t("provider.preview.loginCta")}
                </Button>
            </FrostedCard>
        );
    }

    if (view === "expired") {
        return (
            <FrostedCard sx={{ p: 3, maxWidth: 560, mx: "auto" }}>
                <Alert severity="warning">
                    {isAuthenticated
                        ? t("provider.preview.expiredAuthenticatedNotice")
                        : t("provider.preview.expiredAnonNotice")}
                </Alert>
                <Button
                    sx={[amberCtaSx, { mt: 2 }]}
                    variant="contained"
                    startIcon={<LoginIcon />}
                    loading={busy}
                    onClick={() => handleCta()}
                >
                    {isAuthenticated ? t("provider.preview.openRequestCta") : t("provider.preview.loginCta")}
                </Button>
                {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
            </FrostedCard>
        );
    }

    if (view === "denied") {
        // fb#2313: the usual cause is signing in with the wrong (e.g. personal
        // Google) account. Name it from the visitor's OWN session — the server
        // still answers a bare ok:false, so not_member and member_no_permission
        // stay indistinguishable (spec §5).
        const email = user?.person?.[0]?.personEmail;
        // Stays on this view while the modal is open: it is also reached from the
        // expired view, where there is no preview to render (fb#2322). A
        // successful re-login moves the view on via runAccess.
        const switchAccount = () => {
            logout();
            openAuthModal({ onSuccess: () => setPendingLogin(true) });
        };
        return (
            <FrostedCard sx={{ p: 3, maxWidth: 560, mx: "auto" }}>
                <Alert severity="info">
                    {t("provider.preview.deniedText")}
                    {email && <><br /><br />{t("provider.preview.deniedSignedInAs", { email })}</>}
                </Alert>
                <Button sx={{ mt: 2 }} variant="outlined" startIcon={<LoginIcon />} onClick={switchAccount}>
                    {t("provider.preview.switchAccountCta")}
                </Button>
            </FrostedCard>
        );
    }

    if (view === "unavailable") {
        return (
            <FrostedCard sx={{ p: 3, maxWidth: 560, mx: "auto" }}>
                <Alert severity="info">{t("provider.preview.unavailableText")}</Alert>
            </FrostedCard>
        );
    }

    const { request, ownStatus } = preview;
    const requestOpen = request.requestState === "open";
    const canDraft = requestOpen && ownStatus === "open";
    // DEFAULT-ALLOW. A token minted without a recipientPersonId — every invite
    // sent to a shared inbox (offerNotificationEmail) or a billing address —
    // cannot bid: POST /preview/offer 409s it. Saying so here means the visitor
    // learns it before pricing the job, not after submitting a filled-in form.
    // `!== false` and never truthiness: if this FE ships before the backend that
    // sends the field, an absent value must keep the form, not disable it for
    // everyone. bidRefused is the same answer learned the hard way (a 409).
    const canBidWithLink = preview.canBidWithLink !== false && !bidRefused;
    const declineDialogOpen = declineOpen
        || (declineIntent && !declineIntentDismissed && canDraft && outcome === null);
    // Every login-CTA string has an authenticated twin: never "Kirjaudu sisään"
    // to a visitor who already has a session (owner ruling 2026-10-07).
    const openRequestLabel = isAuthenticated ? t("provider.preview.openRequestCta") : t("provider.preview.loginCta");
    // "Ilmaise kiinnostus" emails the customer, so it is an ACTION: it lives in
    // the action card, last, and only while there is still a decision to make.
    const showInterest = canDraft && outcome === null && !request.isCustomerInfoRevealed;

    return (
        <Grid container spacing={2} sx={{ maxWidth: 1040, mx: "auto" }}>
            <Grid size={12}>
                <Typography component="h1" variant="h5">
                    {t("provider.preview.heading", { id: request.pumppuRequestId })}
                </Typography>
                <Typography sx={{ color: "text.secondary", mt: 0.5 }}>{summaryLine(request, lang, t)}</Typography>
            </Grid>

            {/* Phone: the decision first, the full recap below it (the summary
                line above carries the specs). Desktop: recap left, card right.
                `order` goes through sx: MUI 9's Grid has no order prop and drops
                it silently (verified in the browser — both items computed 0). */}
            <Grid size={{ xs: 12, md: 7 }} sx={{ order: { xs: 2, md: 1 } }}>
                <RequestRecap request={request} requestAttachments={[]} />
                {request.isCustomerInfoRevealed && (
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                        {t("provider.preview.attachmentsRevealedHint")}
                    </Typography>
                )}
            </Grid>

            <Grid size={{ xs: 12, md: 5 }} sx={{ order: { xs: 1, md: 2 } }}>
                <FrostedCard sx={{ p: 2 }}>
                    {!requestOpen && (
                        <Stack spacing={2}>
                            <Alert severity="info">{t("provider.preview.requestEndedNotice")}</Alert>
                            <Button variant="outlined" sx={TAP_TARGET_SX} startIcon={<LoginIcon />} loading={busy} onClick={() => handleCta()}>
                                {openRequestLabel}
                            </Button>
                        </Stack>
                    )}
                    {requestOpen && ownStatus === "offered" && (
                        <Stack spacing={2}>
                            <Alert severity="success">{t("provider.preview.alreadyOfferedNotice")}</Alert>
                            <Button variant="contained" sx={amberCtaSx} startIcon={<LoginIcon />} loading={busy} onClick={() => handleCta()}>
                                {isAuthenticated ? t("provider.preview.viewOfferCta") : t("provider.preview.loginViewOfferCta")}
                            </Button>
                        </Stack>
                    )}
                    {requestOpen && ownStatus === "declined" && (
                        <Stack spacing={2}>
                            <Alert severity="info">{t("provider.preview.declinedNotice")}</Alert>
                            <Button variant="outlined" sx={TAP_TARGET_SX} startIcon={<LoginIcon />} loading={busy} onClick={() => handleCta()}>
                                {openRequestLabel}
                            </Button>
                        </Stack>
                    )}
                    {outcome === "offered" && (
                        <Stack spacing={2}>
                            <Alert severity="success">{t("provider.preview.offerSentNotice")}</Alert>
                            {/* Attachments and the customer chat live in the app; the
                                sent offer is editable there until the customer accepts. */}
                            <Button variant="outlined" sx={TAP_TARGET_SX} startIcon={<LoginIcon />} loading={busy} onClick={() => handleCta()}>
                                {isAuthenticated ? t("provider.preview.openInAppCta") : t("provider.preview.loginOpenInAppCta")}
                            </Button>
                        </Stack>
                    )}
                    {outcome === "declined" && (
                        <Alert severity="info">{t("provider.preview.declineSentNotice")}</Alert>
                    )}
                    {canDraft && outcome === null && (
                        <Stack spacing={2}>
                            {canBidWithLink && !confirming && (
                                <form onSubmit={startSubmitOffer} noValidate>
                                    <Stack spacing={2}>
                                        <Typography variant="subtitle2" component="h2">{t("provider.preview.makeOfferHeading")}</Typography>
                                        <TextField
                                            label={t("provider.shared.priceLabel")}
                                            helperText={priceError ?? t("provider.shared.priceHelper")}
                                            error={Boolean(priceError)}
                                            type="number"
                                            slotProps={{ htmlInput: { min: 1, step: 1, inputMode: "numeric" } }}
                                            value={price}
                                            onChange={(e) => updatePrice(e.target.value)}
                                            fullWidth
                                        />
                                        <TextField
                                            label={t("provider.shared.extraNotesLabel")}
                                            helperText={t("provider.shared.extraNotesHelper")}
                                            value={extraNotes}
                                            onChange={(e) => updateExtraNotes(e.target.value)}
                                            multiline
                                            rows={3}
                                            fullWidth
                                        />
                                        {/* The number is a binding offer on the provider's side
                                            (CLAUDE.md legal model); say so where the decision is
                                            made, not in a footer. */}
                                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            {t("provider.preview.commitmentNotice")}
                                        </Typography>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            startIcon={<SendIcon />}
                                            sx={amberCtaSx}
                                        >
                                            {t("provider.preview.sendOfferCta")}
                                        </Button>
                                    </Stack>
                                </form>
                            )}
                            {canBidWithLink && confirming && (
                                <Stack spacing={2} role="group" aria-labelledby="esikatselu-confirm">
                                    <Typography id="esikatselu-confirm" sx={{ fontWeight: 600 }}>
                                        {t("provider.preview.confirmOfferQuestion", { price: localizeDecimal(priceCents / 100, lang) })}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<SendIcon />}
                                        loading={busy}
                                        sx={amberCtaSx}
                                        onClick={handleSubmitOffer}
                                    >
                                        {t("provider.preview.confirmSendCta")}
                                    </Button>
                                    <Button variant="text" color="inherit" sx={TAP_TARGET_SX} disabled={busy} onClick={() => setConfirming(false)}>
                                        {t("provider.preview.confirmEditCta")}
                                    </Button>
                                </Stack>
                            )}
                            {!canBidWithLink && (
                                <>
                                    {/* The fact, once; the action is the lamp below it.
                                        A shared-inbox token can still decline. */}
                                    <Alert severity="info">{t("provider.preview.cannotBidWithLinkNotice")}</Alert>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        sx={amberCtaSx}
                                        startIcon={<LoginIcon />}
                                        loading={busy}
                                        onClick={() => handleCta()}
                                    >
                                        {isAuthenticated ? t("provider.preview.openRequestCta") : t("provider.preview.loginToOfferCta")}
                                    </Button>
                                </>
                            )}
                            {!confirming && (
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    sx={TAP_TARGET_SX}
                                    startIcon={<BlockIcon />}
                                    loading={busy}
                                    onClick={() => { setActionError(null); setDeclineOpen(true); }}
                                >
                                    {t("provider.shared.declineCta")}
                                </Button>
                            )}
                            {showInterest && !confirming && (
                                <Stack spacing={0.5}>
                                    {interestSent ? (
                                        <Alert severity="success">{t("provider.preview.interestSentNotice")}</Alert>
                                    ) : (
                                        <Button
                                            variant="text"
                                            color="inherit"
                                            sx={TAP_TARGET_SX}
                                            startIcon={<VisibilityIcon />}
                                            loading={revealBusy}
                                            onClick={handleReveal}
                                        >
                                            {t("provider.preview.revealCta")}
                                        </Button>
                                    )}
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {t("provider.preview.revealHint")}
                                    </Typography>
                                    {revealError && <Alert severity="error">{revealError}</Alert>}
                                </Stack>
                            )}
                            {canBidWithLink && !confirming && (
                                <Stack spacing={0.5}>
                                    {/* Attachments and the customer chat are app-only; the
                                        label says what the app adds instead of duplicating
                                        "Lähetä tarjous" with a vaguer verb. */}
                                    <Button variant="text" color="inherit" sx={TAP_TARGET_SX} startIcon={<LoginIcon />} onClick={() => handleCta()}>
                                        {isAuthenticated ? t("provider.preview.openInAppCta") : t("provider.preview.loginOpenInAppCta")}
                                    </Button>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {t("provider.preview.openInAppHint")}
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    )}
                    {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
                </FrostedCard>
            </Grid>

            <Dialog
                open={declineDialogOpen}
                onClose={() => { setDeclineOpen(false); setDeclineIntentDismissed(true); }}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>{t("provider.preview.declineHeading")}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label={t("provider.preview.declineReasonLabel")}
                        helperText={t("provider.preview.declineReasonHelper")}
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                        multiline
                        rows={3}
                        fullWidth
                        sx={{ mt: 1 }}
                    />
                    {actionError && <Alert severity="error" sx={{ mt: 2 }}>{actionError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setDeclineOpen(false); setDeclineIntentDismissed(true); }}>
                        {t("provider.preview.declineCancelCta")}
                    </Button>
                    <Button variant="contained" loading={busy} onClick={handleSubmitDecline}>
                        {t("provider.preview.declineSubmitCta")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}
